import type {
  IAcceptInviteData,
  IAuthMessageResponse,
  IAuthRedirectResponse,
  IExchangeAuthCodeData,
  IExchangeAuthCodeResponse,
  IForgotPasswordData,
  IGetOrganizationSamlParams,
  IGetSingleOrganizationSamlParams,
  IInviteAcceptDetails,
  IRefreshTokenResponse,
  IRegistrationStatusResponse,
  IResetPasswordData,
  ISamlConfig,
  ISamlLoginParams,
  ISamlRedirectQuery,
  ISigninData,
  ISigninResponse,
  ISignupData,
  ISignupResponse,
  ISingleOrganizationSaml,
} from "../types";
import type { ApiClient } from "./client";

// NOTE: The external-client query both SAML redirect lookups take, in the
// snake_case the server reads (source, callback_url, code_challenge).
const toSamlRedirectQuery = ({
  source,
  callbackUrl,
  codeChallenge,
}: IGetSingleOrganizationSamlParams): ISamlRedirectQuery => ({
  ...(source !== undefined && { source }),
  ...(callbackUrl !== undefined && { callback_url: callbackUrl }),
  ...(codeChallenge !== undefined && { code_challenge: codeChallenge }),
});

export class AuthApi {
  constructor(private apiClient: ApiClient) {}

  async signin(data: ISigninData): Promise<ISigninResponse> {
    const response = await this.apiClient.post<ISigninResponse>(
      "/auth/signin",
      data,
    );
    return response.data;
  }

  async signup(data: ISignupData): Promise<ISignupResponse> {
    const response = await this.apiClient.post<ISignupResponse>(
      "/auth/signup",
      data,
    );
    return response.data;
  }

  // NOTE: NSAI-6810 — carries the stored refresh credential (mobile, the
  // extension; the web's rides as its cookie), so the server ends the session
  // even when the access token has expired and no refresh is possible.
  async logout(): Promise<void> {
    const refreshToken = this.apiClient.getStoredRefreshToken();

    await this.apiClient.post(
      "/auth/logout",
      refreshToken ? { refreshToken } : {},
    );
  }

  // NOTE: NSAI-4194 — through the client's shared refresh, so the rotated
  // refresh token is stored (see ApiClient.refreshSession). It used to post
  // /auth/refresh with no body: mobile's sockets then refreshed through the
  // native cookie jar and dropped the new refresh token.
  async refreshToken(): Promise<IRefreshTokenResponse> {
    const accessToken = await this.apiClient.refreshSession();
    return { accessToken };
  }

  async getRegistrationStatus(): Promise<IRegistrationStatusResponse> {
    const response =
      await this.apiClient.get<IRegistrationStatusResponse>("/auth/status");
    return response.data;
  }

  async getSingleOrganizationSaml(
    params?: IGetSingleOrganizationSamlParams,
  ): Promise<ISingleOrganizationSaml | null> {
    const response = await this.apiClient.get<ISingleOrganizationSaml | null>(
      "/auth/saml/organization",
      params ? { params: toSamlRedirectQuery(params) } : undefined,
    );
    return response.data;
  }

  // NOTE: NSAI-7736 — GET /auth/saml/organization/:organizationUuid, the
  // lookup behind an organization's own sign-in link (`/auth/sso/<uuid>` in
  // the apps). Null for an unknown, deleted or unconfigured organization,
  // exactly like the single-organization lookup.
  async getOrganizationSaml({
    organizationUuid,
    ...params
  }: IGetOrganizationSamlParams): Promise<ISingleOrganizationSaml | null> {
    const response = await this.apiClient.get<ISingleOrganizationSaml | null>(
      `/auth/saml/organization/${encodeURIComponent(organizationUuid)}`,
      { params: toSamlRedirectQuery(params) },
    );
    return response.data;
  }

  async forgotPassword(
    data: IForgotPasswordData,
  ): Promise<IAuthMessageResponse> {
    const response = await this.apiClient.post<IAuthMessageResponse>(
      "/auth/forgot-password",
      data,
    );
    return response.data;
  }

  async resetPassword(data: IResetPasswordData): Promise<IAuthMessageResponse> {
    const response = await this.apiClient.post<IAuthMessageResponse>(
      "/auth/reset-password",
      data,
    );
    return response.data;
  }

  async getInviteDetails(token: string): Promise<IInviteAcceptDetails> {
    const response = await this.apiClient.get<IInviteAcceptDetails>(
      `/auth/invite/${token}`,
    );
    return response.data;
  }

  async acceptInvite(data: IAcceptInviteData): Promise<ISigninResponse> {
    const response = await this.apiClient.post<ISigninResponse>(
      "/auth/invite/accept",
      data,
    );
    return response.data;
  }

  async googleLogin(): Promise<IAuthRedirectResponse> {
    const response =
      await this.apiClient.get<IAuthRedirectResponse>("/auth/google");
    return response.data;
  }

  async githubLogin(): Promise<IAuthRedirectResponse> {
    const response =
      await this.apiClient.get<IAuthRedirectResponse>("/auth/github");
    return response.data;
  }

  async saml(data: ISamlLoginParams): Promise<ISamlConfig[]> {
    const response = await this.apiClient.post<ISamlConfig[]>(
      "/auth/saml",
      data,
    );
    return response.data;
  }

  // NOTE: NSAI-6117 — the second half of an external client's SSO / social
  // sign-in. Opted out of the client's 401 handling: no session exists yet, so
  // a refused code is a sign-in that did not happen, never a session to
  // refresh or a reason to sign the device out. The server answers a refusal
  // with 400 for the same reason.
  async exchangeAuthCode(
    data: IExchangeAuthCodeData,
  ): Promise<IExchangeAuthCodeResponse> {
    const response = await this.apiClient.post<IExchangeAuthCodeResponse>(
      "/auth/exchange-code",
      data,
      { skipAuthHandling: true },
    );
    return response.data;
  }
}
